import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Upload } from 'lucide-react';
import { usePublishVersion } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';

/**
 * Mirrors the server's own rules, so a mistake is caught before a round trip.
 *
 * The hash pattern in particular is worth checking here: it is 64 lowercase hex
 * characters, and the most common way to get it wrong is pasting the uppercase
 * output of `certutil` on Windows, which the server rejects with a message
 * about a pattern rather than about case.
 */
const SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/;
const SHA256 = /^[a-f0-9]{64}$/;

const schema = Yup.object({
  versionNumber: Yup.string()
    .matches(SEMVER, 'Use a semantic version, e.g. 1.4.0')
    .required('Required'),
  releaseChannel: Yup.string().oneOf(['stable', 'beta']).required(),
  platform: Yup.string().oneOf(['windows', 'linux', 'macos']).required(),
  installerS3Key: Yup.string().trim().max(500).required('Required'),
  cloudfrontUrl: Yup.string().url('Must be a URL').required('Required'),
  fileHashSha256: Yup.string()
    .matches(SHA256, 'A SHA-256 hash: 64 lowercase hex characters')
    .required('Required'),
  fileSizeBytes: Yup.number().integer().positive('Must be a positive number').required('Required'),
  releaseNotes: Yup.string().max(10000),
  minSupportedVersion: Yup.string().matches(SEMVER, {
    message: 'Use a semantic version',
    excludeEmptyString: true,
  }),
});

/**
 * Publishing a build.
 *
 * This is what makes a version reachable by the desktop update check, which is
 * why the hash and the code-signing flag are recorded here rather than trusted
 * from the client that eventually downloads it.
 *
 * `mandatory` is separated from `codeSigned` on the form because they do very
 * different things: one stops the application until the user updates, the other
 * decides whether Windows will install it at all.
 */
export function PublishVersionDialog({ disabled }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  const publish = usePublishVersion();

  const form = useFormik({
    initialValues: {
      versionNumber: '',
      releaseChannel: 'beta' as 'stable' | 'beta',
      platform: 'windows' as 'windows' | 'linux' | 'macos',
      installerS3Key: '',
      cloudfrontUrl: '',
      fileHashSha256: '',
      fileSizeBytes: '',
      releaseNotes: '',
      minSupportedVersion: '',
      codeSigned: true,
      mandatory: false,
    },
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      try {
        await publish.mutateAsync({
          versionNumber: values.versionNumber.trim(),
          releaseChannel: values.releaseChannel,
          platform: values.platform,
          installerS3Key: values.installerS3Key.trim(),
          cloudfrontUrl: values.cloudfrontUrl.trim(),
          fileHashSha256: values.fileHashSha256.trim().toLowerCase(),
          // The server coerces to BigInt; a string is what survives JSON intact.
          fileSizeBytes: values.fileSizeBytes,
          codeSigned: values.codeSigned,
          mandatory: values.mandatory,
          ...(values.releaseNotes.trim() ? { releaseNotes: values.releaseNotes.trim() } : {}),
          ...(values.minSupportedVersion.trim()
            ? { minSupportedVersion: values.minSupportedVersion.trim() }
            : {}),
        } as Parameters<typeof publish.mutateAsync>[0]);
        helpers.resetForm();
        setOpen(false);
      } catch (error) {
        if (error instanceof ApiError && error.fieldErrors) {
          const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), form.values);
          helpers.setErrors(placed);
        }
      }
    },
  });

  const field = (name: keyof typeof form.values, label: string, placeholder?: string) => (
    <div className="grid gap-2">
      <Label htmlFor={`version-${name}`}>{label}</Label>
      <Input
        id={`version-${name}`}
        name={name}
        value={String(form.values[name])}
        onChange={form.handleChange}
        onBlur={form.handleBlur}
        placeholder={placeholder}
        aria-invalid={Boolean(form.touched[name] && form.errors[name])}
      />
      {form.touched[name] && form.errors[name] && (
        <p className="text-sm text-destructive">{String(form.errors[name])}</p>
      )}
    </div>
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button className="ml-auto" disabled={disabled}>
          <Upload className="size-4" />
          Publish version
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={form.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Publish a version</DialogTitle>
            <DialogDescription>
              Once published, the desktop update check can offer this build. It cannot be
              unpublished — release a newer version instead.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              {field('versionNumber', 'Version', '1.4.0')}

              <div className="grid gap-2">
                <Label htmlFor="version-channel">Channel</Label>
                <Select
                  value={form.values.releaseChannel}
                  onValueChange={(value) => {
                    void form.setFieldValue('releaseChannel', value);
                  }}
                >
                  <SelectTrigger id="version-channel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beta">Beta</SelectItem>
                    <SelectItem value="stable">Stable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* One installer per platform per version: 1.4.0 has a Windows and a Linux build. */}
            <div className="grid gap-2">
              <Label htmlFor="version-platform">Platform</Label>
              <Select
                value={form.values.platform}
                onValueChange={(value) => {
                  void form.setFieldValue('platform', value);
                }}
              >
                <SelectTrigger id="version-platform">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="windows">Windows</SelectItem>
                  <SelectItem value="linux">Linux</SelectItem>
                  <SelectItem value="macos">macOS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {field('installerS3Key', 'Installer S3 key', 'installers/nudent-1.4.0.exe')}
            {field('cloudfrontUrl', 'Download URL', 'https://dl.nudent.ai/nudent-1.4.0.exe')}
            {field('fileHashSha256', 'SHA-256', '64 lowercase hex characters')}
            {field('fileSizeBytes', 'Size in bytes', '184320000')}
            {field('minSupportedVersion', 'Minimum supported version (optional)', '1.2.0')}

            <div className="grid gap-2">
              <Label htmlFor="version-notes">Release notes</Label>
              <textarea
                id="version-notes"
                name="releaseNotes"
                rows={3}
                value={form.values.releaseNotes}
                onChange={form.handleChange}
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  name="codeSigned"
                  checked={form.values.codeSigned}
                  onChange={form.handleChange}
                  className="size-4"
                />
                Code signed
              </label>
              <p className="text-xs text-muted-foreground">
                An unsigned installer is refused by Windows on the customer&rsquo;s machine.
              </p>

              <label className="flex items-center gap-2 pt-2 text-sm">
                <input
                  type="checkbox"
                  name="mandatory"
                  checked={form.values.mandatory}
                  onChange={form.handleChange}
                  className="size-4"
                />
                Mandatory
              </label>
              <p className="text-xs text-muted-foreground">
                Stops the application until the user updates, rather than showing a banner.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isSubmitting || publish.isPending}>
              Publish
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
